/**
 * Finance → Project Spend — Feb 2026
 *
 * Rolls up every reimbursable vendor bill under its project so PMs can
 * see billable-vs-margin BEFORE invoicing the client. Columns:
 *   Project | Client | Reimbursable bills (count · ₹) | Invoiced through
 *   | Pending to invoice | Revenue | Margin (% + ₹)
 *
 * Why not inside Project detail: a workspace with 100+ projects needs a
 * single sortable scroll surface. Clicking a row deep-links into
 * `/app/projects/<id>` for the full project view.
 */
import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Loader2, ExternalLink, Filter, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { apiErrorMessage } from "@/lib/apiError";
import { useAuth } from "@/lib/auth";
import { clientsLookup } from "@/lib/lookupCache";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import EmptyState from "@/components/EmptyState";

function _inr(n) {
  const v = Number(n || 0);
  return `₹${v.toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;
}

export default function FinanceProjectSpend() {
  const { activeWorkspaceId } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [totals, setTotals] = useState(null);
  const [clients, setClients] = useState([]);
  const [clientFilter, setClientFilter] = useState("");
  const [includeZero, setIncludeZero] = useState(false);
  const [q, setQ] = useState("");
  const [sortBy, setSortBy] = useState("pending");
  const [sortDir, setSortDir] = useState("desc");

  useEffect(() => {
    if (!activeWorkspaceId) return;
    clientsLookup(activeWorkspaceId).then((c) => setClients(c || []));
  }, [activeWorkspaceId]);

  const load = async () => {
    if (!activeWorkspaceId) return;
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (clientFilter) params.set("client_id", clientFilter);
      if (includeZero) params.set("include_zero", "1");
      const { data } = await api.get(
        `/finance/workspaces/${activeWorkspaceId}/project-spend?${params.toString()}`,
      );
      setRows(data.rows || []);
      setTotals(data.totals || null);
    } catch (e) {
      toast.error(apiErrorMessage(e, "Failed to load project spend"));
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [activeWorkspaceId, clientFilter, includeZero]);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    let list = s
      ? rows.filter((r) =>
          (r.project_name || "").toLowerCase().includes(s)
          || (r.client_name || "").toLowerCase().includes(s))
      : rows;
    const dir = sortDir === "asc" ? 1 : -1;
    const key = {
      project: (r) => (r.project_name || "").toLowerCase(),
      client: (r) => (r.client_name || "").toLowerCase(),
      bills: (r) => r.bills_total || 0,
      reimb: (r) => r.bills_reimbursable_total || 0,
      invoiced: (r) => r.invoiced_through_total || 0,
      pending: (r) => r.pending_to_invoice_total || 0,
      revenue: (r) => r.revenue_total || 0,
      margin: (r) => r.margin || 0,
      marginPct: (r) => (r.margin_pct ?? -9999),
    }[sortBy] || ((r) => r.pending_to_invoice_total || 0);
    return [...list].sort((a, b) => {
      const av = key(a), bv = key(b);
      if (typeof av === "string") return av.localeCompare(bv) * dir;
      return (av - bv) * dir;
    });
  }, [rows, q, sortBy, sortDir]);

  const toggleSort = (col) => {
    if (sortBy === col) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else { setSortBy(col); setSortDir(col === "project" || col === "client" ? "asc" : "desc"); }
  };

  return (
    <div className="p-4 md:p-6 space-y-4" data-testid="project-spend-page">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Finance · Purchases AP</div>
          <h1 className="text-2xl md:text-3xl font-bold tracking-tight">Project Spend</h1>
          <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
            Reimbursable vendor bills rolled up per project so you see billable-vs-margin before invoicing the client.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={load}
                   className="rounded-none"
                   data-testid="project-spend-refresh">
            <RefreshCw className="w-3.5 h-3.5 mr-1.5" /> Refresh
          </Button>
        </div>
      </div>

      {/* Top-line totals */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="project-spend-totals">
        <StatCard label="Reimbursable bills" value={_inr(totals?.bills_reimbursable_total)} testid="ps-stat-reimb" />
        <StatCard label="Invoiced through" value={_inr(totals?.invoiced_through_total)} testid="ps-stat-invoiced" />
        <StatCard label="Pending to invoice" value={_inr(totals?.pending_to_invoice_total)} testid="ps-stat-pending"
                   tone={(totals?.pending_to_invoice_total || 0) > 0 ? "amber" : "neutral"} />
        <StatCard label="Revenue (invoiced)" value={_inr(totals?.revenue_total)} testid="ps-stat-revenue" />
        <StatCard label="Margin" value={_inr(totals?.margin)} testid="ps-stat-margin"
                   tone={(totals?.margin || 0) >= 0 ? "emerald" : "rose"} />
      </div>

      {/* Filter bar */}
      <div className="flex flex-wrap items-end gap-2 border border-border p-2 bg-muted/30">
        <div className="flex items-center gap-1 text-[10px] uppercase tracking-widest text-muted-foreground">
          <Filter className="w-3 h-3" /> Filters
        </div>
        <Select value={clientFilter || "all"} onValueChange={(v) => setClientFilter(v === "all" ? "" : v)}>
          <SelectTrigger className="h-9 w-[220px] text-xs rounded-none" data-testid="ps-filter-client">
            <SelectValue placeholder="All clients" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All clients</SelectItem>
            {clients.map((c) => (
              <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Input placeholder="Search project / client…" value={q} onChange={(e) => setQ(e.target.value)}
                 className="h-9 w-[260px] text-xs rounded-none" data-testid="ps-search" />
        <label className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-muted-foreground cursor-pointer ml-2">
          <input type="checkbox" checked={includeZero}
                  onChange={(e) => setIncludeZero(e.target.checked)}
                  className="w-3.5 h-3.5"
                  data-testid="ps-include-zero" />
          Include zero-activity projects
        </label>
        <div className="ml-auto text-xs text-muted-foreground tabular-nums">
          {loading ? "Loading…" : `${filtered.length} project${filtered.length === 1 ? "" : "s"}`}
        </div>
      </div>

      {/* Table */}
      <div className="border border-border overflow-x-auto" data-testid="project-spend-table-wrap">
        <table className="w-full text-xs border-collapse" style={{ minWidth: 1200 }}>
          <thead className="bg-muted/60">
            <tr className="text-left">
              <ThSort label="Project" col="project" sortBy={sortBy} sortDir={sortDir} onClick={toggleSort} />
              <ThSort label="Client" col="client" sortBy={sortBy} sortDir={sortDir} onClick={toggleSort} />
              <ThSort label="Reimbursable (count · ₹)" col="reimb" sortBy={sortBy} sortDir={sortDir} onClick={toggleSort} align="right" />
              <ThSort label="Invoiced through" col="invoiced" sortBy={sortBy} sortDir={sortDir} onClick={toggleSort} align="right" />
              <ThSort label="Pending to invoice" col="pending" sortBy={sortBy} sortDir={sortDir} onClick={toggleSort} align="right" />
              <ThSort label="Revenue" col="revenue" sortBy={sortBy} sortDir={sortDir} onClick={toggleSort} align="right" />
              <ThSort label="Margin" col="margin" sortBy={sortBy} sortDir={sortDir} onClick={toggleSort} align="right" />
              <ThSort label="Margin %" col="marginPct" sortBy={sortBy} sortDir={sortDir} onClick={toggleSort} align="right" />
              <th className="px-2 py-2 border-b border-border w-[48px]"></th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={9} className="px-3 py-10 text-center text-muted-foreground">
                <Loader2 className="inline w-4 h-4 animate-spin mr-2" /> Loading project spend…
              </td></tr>
            )}
            {!loading && filtered.length === 0 && (
              <tr><td colSpan={9} className="px-3 py-10">
                <EmptyState title="No project spend yet"
                             description="Reimbursable bills tagged to a project will roll up here. Open a bill and set it as reimbursable to see margin here." />
              </td></tr>
            )}
            {!loading && filtered.map((r) => (
              <tr key={r.project_id} className="border-b border-border/60 hover:bg-muted/30 cursor-pointer"
                  onClick={() => navigate(`/app/projects/${r.project_id}`)}
                  data-testid={`ps-row-${r.project_id}`}>
                <td className="px-3 py-2 font-medium">{r.project_name || <span className="text-muted-foreground italic">Untitled</span>}</td>
                <td className="px-3 py-2 text-muted-foreground">{r.client_name || "—"}</td>
                <td className="px-3 py-2 text-right tabular-nums">
                  <span className="text-muted-foreground mr-1">{r.bills_reimbursable_count}×</span>
                  <span className="font-mono">{_inr(r.bills_reimbursable_total)}</span>
                </td>
                <td className="px-3 py-2 text-right tabular-nums font-mono">{_inr(r.invoiced_through_total)}</td>
                <td className={`px-3 py-2 text-right tabular-nums font-mono ${(r.pending_to_invoice_total || 0) > 0 ? "text-amber-700 font-semibold" : "text-muted-foreground"}`}
                     data-testid={`ps-pending-${r.project_id}`}>
                  {_inr(r.pending_to_invoice_total)}
                </td>
                <td className="px-3 py-2 text-right tabular-nums font-mono">{_inr(r.revenue_total)}</td>
                <td className={`px-3 py-2 text-right tabular-nums font-mono font-semibold ${
                  (r.margin || 0) > 0 ? "text-emerald-700" : (r.margin || 0) < 0 ? "text-rose-700" : "text-muted-foreground"
                }`} data-testid={`ps-margin-${r.project_id}`}>
                  {_inr(r.margin)}
                </td>
                <td className={`px-3 py-2 text-right tabular-nums font-mono ${
                  r.margin_pct === null ? "text-muted-foreground" :
                  r.margin_pct >= 20 ? "text-emerald-700" :
                  r.margin_pct >= 0 ? "text-amber-700" : "text-rose-700"
                }`}>
                  {r.margin_pct === null ? "—" : `${r.margin_pct}%`}
                </td>
                <td className="px-2 py-2 text-right">
                  <Link to={`/app/projects/${r.project_id}`}
                         onClick={(e) => e.stopPropagation()}
                         className="inline-flex items-center text-muted-foreground hover:text-primary"
                         title="Open project"
                         data-testid={`ps-open-${r.project_id}`}>
                    <ExternalLink className="w-3.5 h-3.5" />
                  </Link>
                </td>
              </tr>
            ))}
          </tbody>
          {!loading && filtered.length > 0 && (
            <tfoot className="bg-muted/40 border-t-2 border-border">
              <tr className="font-semibold">
                <td className="px-3 py-2" colSpan={2}>Totals ({filtered.length})</td>
                <td className="px-3 py-2 text-right tabular-nums font-mono">
                  {_inr(filtered.reduce((a, r) => a + (r.bills_reimbursable_total || 0), 0))}
                </td>
                <td className="px-3 py-2 text-right tabular-nums font-mono">
                  {_inr(filtered.reduce((a, r) => a + (r.invoiced_through_total || 0), 0))}
                </td>
                <td className="px-3 py-2 text-right tabular-nums font-mono text-amber-700">
                  {_inr(filtered.reduce((a, r) => a + (r.pending_to_invoice_total || 0), 0))}
                </td>
                <td className="px-3 py-2 text-right tabular-nums font-mono">
                  {_inr(filtered.reduce((a, r) => a + (r.revenue_total || 0), 0))}
                </td>
                <td className="px-3 py-2 text-right tabular-nums font-mono">
                  {_inr(filtered.reduce((a, r) => a + (r.margin || 0), 0))}
                </td>
                <td colSpan={2}></td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>
    </div>
  );
}

function StatCard({ label, value, tone = "neutral", testid }) {
  const toneCls = {
    neutral: "bg-background border-border",
    amber: "bg-amber-50 border-amber-200",
    emerald: "bg-emerald-50 border-emerald-200",
    rose: "bg-rose-50 border-rose-200",
  }[tone] || "bg-background border-border";
  return (
    <div className={`border p-3 ${toneCls}`} data-testid={testid}>
      <div className="text-[10px] uppercase tracking-widest text-muted-foreground">{label}</div>
      <div className="text-xl font-bold font-mono tabular-nums mt-1">{value}</div>
    </div>
  );
}

function ThSort({ label, col, sortBy, sortDir, onClick, align = "left" }) {
  const active = sortBy === col;
  return (
    <th className={`px-3 py-2 border-b border-border text-[10px] uppercase tracking-widest font-semibold cursor-pointer select-none hover:bg-muted ${align === "right" ? "text-right" : "text-left"}`}
         onClick={() => onClick(col)}
         data-testid={`ps-th-${col}`}>
      {label}
      {active && <span className="ml-1 text-primary">{sortDir === "asc" ? "↑" : "↓"}</span>}
    </th>
  );
}
